import { useRef } from 'react';

export function FishboneDiagram({ diagram, onCauseClick }) {
  const svgRef = useRef(null);
  const categories = diagram?.categories || [];
  const topCats = categories.filter((_, i) => i % 2 === 0);
  const bottomCats = categories.filter((_, i) => i % 2 === 1);

  const W = 1100, H = 520;
  const spineY = H / 2;
  const spineStart = 80, spineEnd = W - 40;
  const headX = spineEnd;

  const catSpacing = (spineEnd - spineStart - 100) / Math.max(categories.length, 1);

  const renderBranch = (cat, catIdx, isTop) => {
    const globalIdx = isTop ? catIdx * 2 : catIdx * 2 + 1;
    const x = spineStart + 60 + globalIdx * catSpacing;
    const endY = isTop ? spineY - 130 : spineY + 130;
    const dir = isTop ? -1 : 1;

    return (
      <g key={cat.id} data-testid={`fishbone-branch-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}>
        <line x1={x} y1={spineY} x2={x + 40} y2={endY} stroke={cat.color} strokeWidth="2.5" opacity="0.8" />
        <foreignObject x={x - 10} y={isTop ? endY - 34 : endY + 6} width="120" height="30">
          <div style={{
            fontSize: '11px', fontWeight: 700, color: cat.color,
            textAlign: 'center', fontFamily: 'Outfit, sans-serif',
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            {cat.name}
          </div>
        </foreignObject>

        {(cat.causes || []).slice(0, 5).map((cause, ci) => {
          const frac = (ci + 1) / (Math.min(cat.causes.length, 5) + 1);
          const boneX = x + 40 * frac;
          const boneY = spineY + (endY - spineY) * frac;
          const branchLen = 65;
          const branchEndX = boneX - branchLen;

          return (
            <g key={cause.id} className="cursor-pointer" onClick={() => onCauseClick(cat, cause)}>
              <line x1={boneX} y1={boneY} x2={branchEndX} y2={boneY}
                stroke={cat.color} strokeWidth="1.5" opacity="0.6" />
              <circle cx={branchEndX} cy={boneY} r={cause.is_root_cause ? 6 : cause.is_contributing_factor ? 5 : 3.5}
                fill={cause.is_root_cause ? '#dc2626' : cause.is_contributing_factor ? '#f59e0b' : cat.color}
                stroke="white" strokeWidth="1.5" className="hover:r-5 transition-all" />
              <foreignObject x={branchEndX - 95} y={boneY - 10} width="90" height="22">
                <div style={{
                  fontSize: '9px', color: '#475569', textAlign: 'right',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  lineHeight: '20px',
                }} title={cause.text}>
                  {cause.text}
                </div>
              </foreignObject>

              {(cause.sub_causes || []).slice(0, 3).map((sub, si) => {
                const subX = branchEndX + (boneX - branchEndX) * ((si + 1) / 4);
                const subEndY = boneY + dir * 18;
                return (
                  <g key={sub.id}>
                    <line x1={subX} y1={boneY} x2={subX} y2={subEndY} stroke={cat.color} strokeWidth="1" opacity="0.4" />
                    <circle cx={subX} cy={subEndY} r="2" fill={cat.color} opacity="0.5" />
                  </g>
                );
              })}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="fishbone-diagram-container">
      <div className="overflow-x-auto">
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 700, minHeight: 350 }}>
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#334155" />
            </marker>
          </defs>

          <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#f1f5f9" strokeWidth="0.5" />
          </pattern>
          <rect width={W} height={H} fill="url(#grid)" />

          <line x1={spineStart} y1={spineY} x2={headX} y2={spineY}
            stroke="#334155" strokeWidth="3" markerEnd="url(#arrowhead)" />

          <foreignObject x={headX - 30} y={spineY - 30} width="70" height="60">
            <div style={{
              background: 'linear-gradient(135deg, #1e293b, #334155)',
              borderRadius: '8px', padding: '6px 8px',
              color: 'white', fontSize: '9px', fontWeight: 600,
              textAlign: 'center', lineHeight: 1.3,
              fontFamily: 'Outfit, sans-serif',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%',
            }}>
              EFFECT
            </div>
          </foreignObject>

          {topCats.map((cat, i) => renderBranch(cat, i, true))}
          {bottomCats.map((cat, i) => renderBranch(cat, i, false))}

          <g transform={`translate(${spineStart}, ${H - 35})`}>
            <circle cx="0" cy="0" r="5" fill="#dc2626" stroke="white" strokeWidth="1" />
            <text x="10" y="4" fill="#64748b" fontSize="9">Root Cause</text>
            <circle cx="90" cy="0" r="4" fill="#f59e0b" stroke="white" strokeWidth="1" />
            <text x="100" y="4" fill="#64748b" fontSize="9">Contributing</text>
            <circle cx="180" cy="0" r="3" fill="#94a3b8" stroke="white" strokeWidth="1" />
            <text x="188" y="4" fill="#64748b" fontSize="9">Cause</text>
          </g>
        </svg>
      </div>

      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-center">
        <span className="text-xs text-slate-500">Effect: </span>
        <span className="text-sm font-medium text-slate-800">{diagram?.effect || 'Not defined'}</span>
      </div>
    </div>
  );
}
